// Sales Performance & Incentive Management — data hooks (client, 2026-10-06).
// Server: src/api-sales-performance/index.js. All maths happens there
// (shared/sales-performance/engine.js); the screens only display it.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface Metrics {
  target: number; targetCount: number; newSalesTarget?: number; newCustomerTarget?: number;
  grossSales: number; newSales: number; existingSales: number;
  newSalesPct: number | null; existingSalesPct: number | null;
  invoices: number; newCustomers: number; existingCustomers: number; conversions: number;
  won: number; lost: number; conversionRatePct: number | null; achievementPct: number | null;
  avgDealValue: number; followupsDue: number; followupsDone: number;
  activeMonths?: number; avgMonthlySales?: number;
}

export interface PerfRow {
  employeeId: string; name: string; username: string; department: string; role: string;
  employeeCode: string; designation: string; territory: string; branch: string; managerName: string; managerId: string;
  metrics: Metrics; score: number | null; grade: string; rank: number;
  scoreParts: Record<string, number> | null;
  incentive: number; incentiveStatus: string | null; incentiveId: string | null;
}

export interface MonthPoint {
  key: string; label: string; quarter: string; closed: boolean;
  grossSales: number; newSales: number; existingSales: number; target: number; achievementPct: number | null;
  conversions: number; newConversions: number; existingConversions: number; won: number; lost: number;
  conversionRatePct: number | null; incentive: number;
}

export interface Overview {
  fy: string; view: 'month' | 'quarter' | 'year'; month: string; quarter: string; months: string[]; selected: string[];
  rulesVersion: number; metric: string;
  access: { manage: boolean; finance: boolean; seesAll: boolean; admin: boolean };
  kpis: null | {
    totalSales: number; newSales: number; existingSales: number; target: number; achievementPct: number | null;
    conversions: number; newConversions: number; existingConversions: number; conversionRatePct: number | null;
    avgMonthlySales: number; incentiveEarned: number; incentivePending: number; incentivePaid: number;
    topPerformer: { employeeId: string; name: string; score: number } | null; employees: number;
  };
  rows: PerfRow[];
  monthly: MonthPoint[];
  quarterly: Array<Metrics & { quarter: string; incentive: number }>;
  alerts: Array<{ tone: 'good' | 'warn' | 'bad' | 'info'; text: string }>;
  distribution: { excellent: number; good: number; average: number; below: number };
  pendingApprovals: number;
  closedMonths: string[];
  comparisons: null | Record<'department' | 'territory' | 'branch' | 'manager', ComparisonRow[]>;
  filterOptions: { departments: string[]; territories: string[]; branches: string[]; managers: Array<{ id: string; name: string }>; employees: Array<{ id: string; name: string }> };
}

export interface ComparisonRow { key: string; employees: number; sales: number; newSales: number; target: number; achievementPct: number | null; conversions: number; incentive: number }

export interface OverviewFilters { fy: string; view: string; month?: string; quarter?: string; employee?: string; metric?: string; department?: string; territory?: string; branch?: string; manager?: string }

export function useOverview(f: OverviewFilters) {
  return useQuery({
    queryKey: ['sales-performance', 'overview', f],
    queryFn: async () => {
      const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v) as [string, string][]);
      return (await api.get<Overview>(`/api/sales-performance/overview?${p}`)).data;
    },
  });
}

export interface IncentiveLine { code: string; label: string; amount: number; note?: string }
export interface Breakdown {
  eligible: boolean; reason: string; slab: { fromPct: number; toPct: number | null; ratePct: number } | null;
  eligibleSales: number; lines: IncentiveLine[]; base: number; bonuses: number; deductions: number; capped: boolean; final: number;
}
export interface HistoryEntry { status: string; action: string; byName: string; at: string; reason: string }
export interface Incentive {
  _id: string; employeeId: string; employeeName: string; periodKey: string; fy: string; quarter: string;
  metrics: Metrics; breakdown: Breakdown; base: number; bonuses: number; deductions: number; finalAmount: number; originalAmount: number;
  adjustments: Array<{ label: string; amount: number; reason: string; byName: string; at: string }>;
  status: string; history: HistoryEntry[]; paidAt: string | null; transactionRef: string; rank: number | null;
}

export function useIncentives(f: { fy?: string; month?: string; status?: string }) {
  return useQuery({
    queryKey: ['sales-performance', 'incentives', f],
    queryFn: async () => {
      const p = new URLSearchParams(Object.entries(f).filter(([, v]) => v) as [string, string][]);
      return (await api.get<{ items: Incentive[]; access: { manage: boolean; finance: boolean; admin: boolean } }>(`/api/sales-performance/incentives?${p}`)).data;
    },
  });
}

export function useEmployeePerformance(id: string, fy: string) {
  return useQuery({
    queryKey: ['sales-performance', 'employee', id, fy],
    queryFn: async () => (await api.get(`/api/sales-performance/employees/${id}?fy=${fy}`)).data as EmployeeProfile,
    enabled: !!id,
  });
}

export interface EmployeeMonth extends Metrics {
  key: string; label: string; quarter: string; score: number | null; grade: string; rank: number | null;
  incentive: number; incentiveStatus: string; breakdown: Breakdown; incentiveId: string | null;
  history: HistoryEntry[]; adjustments: Incentive['adjustments'];
}
export interface EmployeeProfile {
  fy: string;
  employee: { id: string; name: string; username: string; role: string; department: string; joined: string | null; employeeCode: string; designation: string; territory: string; branch: string; managerName: string };
  current: EmployeeMonth; monthly: EmployeeMonth[];
  quarterly: Array<Metrics & { quarter: string; incentive: number }>;
  year: Metrics & { score: { score: number | null; grade: string; parts: Record<string, number> | null } };
  incentiveSummary: { earned: number; paid: number; pending: number; rejected: number; avgMonthly: number; highest: number; bestMonth: string | null };
}

export interface Rules {
  salesBasis: 'taxable' | 'grand'; eligibleSales: 'gross' | 'new'; minAchievementPct: number; oldCustomersBefore: string; autoCloseDay: number;
  slabs: Array<{ fromPct: number; toPct: number | null; ratePct: number }>;
  fixedIncentive: number; newCustomerBonus: number; existingCustomerBonus: number; conversionBonus: number;
  monthlyBonus: { minPct: number; amount: number }; quarterlyBonus: { minPct: number; amount: number }; annualBonus: { minPct: number; amount: number };
  topPerformerBonus: number; maxIncentive: number;
  scoreWeights: { achievement: number; conversion: number; newCustomer: number; retention: number; activity: number };
  grades: Array<{ grade: string; minScore: number }>;
}

export function useRules() {
  return useQuery({
    queryKey: ['sales-performance', 'rules'],
    queryFn: async () => (await api.get<{ version: number; rules: Rules; history: Array<{ version: number; note: string; createdAt: string }>; canEdit: boolean }>('/api/sales-performance/rules')).data,
  });
}

export function useAudit(enabled: boolean) {
  return useQuery({
    queryKey: ['sales-performance', 'audit'],
    queryFn: async () => (await api.get<{ items: Array<{ _id: string; action: string; actorName: string; employeeName: string; periodKey: string; before: unknown; after: unknown; reason: string; ip: string; at: string }> }>('/api/sales-performance/audit')).data.items,
    enabled,
  });
}

/** Any write; refreshes every Performance query afterwards. */
export function usePerfMutation<T = unknown>(fn: (body: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-performance'] }) });
}

export const perfApi = {
  calculate: (month: string) => api.post('/api/sales-performance/incentives/calculate', { month }).then((r) => r.data),
  action: (id: string, action: string, extra: { reason?: string; transactionRef?: string } = {}) => api.post(`/api/sales-performance/incentives/${id}/action`, { action, ...extra }).then((r) => r.data),
  adjust: (id: string, body: { label: string; amount: number; reason: string }) => api.post(`/api/sales-performance/incentives/${id}/adjust`, body).then((r) => r.data),
  closeMonth: (key: string) => api.post(`/api/sales-performance/months/${key}/close`).then((r) => r.data),
  reopenMonth: (key: string, reason: string) => api.post(`/api/sales-performance/months/${key}/reopen`, { reason }).then((r) => r.data),
  saveRules: (rules: Rules, note: string) => api.put('/api/sales-performance/rules', { rules, note }).then((r) => r.data),
};

export const STATUS_LABEL: Record<string, string> = {
  projected: 'Projected', calculated: 'Calculated', under_review: 'Under review', manager_approved: 'Manager approved',
  finance_approved: 'Finance approved', approved: 'Approved', paid: 'Paid', rejected: 'Rejected', on_hold: 'On hold', excluded: 'Excluded',
};
export const STATUS_TONE: Record<string, string> = {
  projected: 'bg-slate-100 text-slate-600', calculated: 'bg-blue-50 text-blue-700', under_review: 'bg-amber-50 text-amber-700',
  manager_approved: 'bg-amber-50 text-amber-800', finance_approved: 'bg-indigo-50 text-indigo-700', approved: 'bg-emerald-50 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-800', rejected: 'bg-rose-50 text-rose-700', on_hold: 'bg-slate-200 text-slate-700', excluded: 'bg-slate-100 text-slate-500 font-medium',
};

/** Green ≥ 100 %, amber 80–99 %, red below; grey without a target. */
export function achievementTone(pct: number | null | undefined) {
  if (pct === null || pct === undefined) return 'text-slate-400';
  if (pct >= 100) return 'text-emerald-700';
  if (pct >= 80) return 'text-amber-600';
  return 'text-rose-600';
}

export function currentFy(): string {
  const now = new Date(Date.now() + 330 * 60000);
  const y = now.getUTCFullYear(); const m = now.getUTCMonth() + 1;
  const start = m >= 4 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

export const inr = (n: number | null | undefined) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
/** The month now running (IST), YYYY-MM — its figures are still growing. */
export const currentMonthKey = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 7);
export const monthName = (k: string) => {
  const [y, m] = String(k || '').split('-').map(Number);
  return m ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${y}` : '';
};
export const pctText = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${n}%`);

// ─── Team setup & targets ───────────────────────────────────────────────────

export interface TeamRow { id: string; name: string; username: string; role: string; department: string; employeeCode: string; designation: string; managerId: string; managerName: string; territory: string; branch: string; joiningDate: string | null }
export function useTeam(enabled = true) {
  return useQuery({
    queryKey: ['sales-performance', 'team'],
    queryFn: async () => (await api.get<{ rows: TeamRow[]; managers: Array<{ id: string; name: string; role: string }>; canEdit: boolean }>('/api/sales-performance/team')).data,
    enabled,
  });
}

export interface TargetCell { amount: number; count: number; newSalesTarget: number; newCustomerTarget: number }
export interface TargetsGrid { fy: string; months: Array<{ key: string; label: string; quarter: string }>; rows: Array<{ id: string; name: string; department: string; territory: string; managerName: string; months: Record<string, TargetCell> }>; canEdit: boolean }
export function useTargetsGrid(fy: string) {
  return useQuery({ queryKey: ['sales-performance', 'targets', fy], queryFn: async () => (await api.get<TargetsGrid>(`/api/sales-performance/targets?fy=${fy}`)).data });
}

export const teamApi = {
  saveProfile: (userId: string, body: Partial<TeamRow> & { reason?: string }) => api.put(`/api/sales-performance/team/${userId}`, body).then((r) => r.data),
  saveTargets: (rows: Array<{ userId: string; periodKey: string } & Partial<TargetCell>>, reason: string) => api.put('/api/sales-performance/targets', { rows, reason }).then((r) => r.data),
};

export const errText = (err: unknown, fallback: string) => (err as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

// ─── Daily work monitor (client, 2026-10-28) ────────────────────────────────
// Server: GET /api/sales-performance/activity (src/shared/sales-performance/work-monitor.js).
export type WorkPreset = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'custom';
export interface WorkPeriod { preset: WorkPreset; from?: string; to?: string }
export type WorkMetric =
  | 'newLeads' | 'followupsDone' | 'followupsAdded' | 'quotations' | 'quotationsNoFollowup' | 'proformas' | 'proformasNoFollowup'
  | 'wonRecords' | 'lostRecords'
  | 'overdueFollowups' | 'dueTodayFollowups' | 'untouchedLeads' | 'noProduct' | 'noContact' | 'noNextFollowup' | 'openRecords';
export type WorkRow = { employeeId: string; name: string; oldestUntouchedDays: number } & Record<WorkMetric, number>;
export interface WorkMonitor {
  /** asOf: the moment the backlog is measured at — the end of the period, or now. */
  period: { preset: WorkPreset; start: string; end: string; asOf?: string; asOfIsNow?: boolean };
  metrics: Record<WorkMetric, string>;
  rows: WorkRow[];
  totals: Record<WorkMetric | 'oldestUntouchedDays', number>;
  seesAll: boolean;
}
export interface WorkItem {
  kind: 'record' | 'followup' | 'quotation' | 'proforma';
  id: string; title: string; sub?: string; date?: string | null; ageDays?: number | null; note?: string;
  amount?: number; parentType?: string; parentId?: string; employee?: string;
  /** The page to open — the lead, quotation, proforma, order or invoice. */
  link?: string | null;
}

const workQuery = (p: WorkPeriod) => {
  const q = new URLSearchParams({ preset: p.preset });
  if (p.preset === 'custom') { if (p.from) q.set('from', p.from); if (p.to) q.set('to', p.to); }
  return q;
};

export function useWorkMonitor(p: WorkPeriod) {
  return useQuery({
    queryKey: ['sales-performance', 'activity', p],
    enabled: p.preset !== 'custom' || Boolean(p.from),
    queryFn: async () => (await api.get<WorkMonitor>(`/api/sales-performance/activity?${workQuery(p)}`)).data,
  });
}

export interface WorkDay {
  date: string;
  newLeads: number; followupsAdded: number; followupsDone: number; quotations: number; proformas: number;
  dueTodayFollowups: number; overdueFollowups: number; untouchedLeads: number; noNextFollowup: number;
  quotationsNoFollowup: number; openRecords: number;
}

/** One row per date of the period (team, or one salesperson). */
export function useWorkDays(p: WorkPeriod, employee: string | null) {
  return useQuery({
    queryKey: ['sales-performance', 'activity-days', p, employee],
    enabled: p.preset !== 'custom' || Boolean(p.from),
    queryFn: async () => {
      const q = workQuery(p);
      if (employee) q.set('employee', employee);
      return (await api.get<{ days: WorkDay[] }>(`/api/sales-performance/activity/days?${q}`)).data;
    },
  });
}

export function useWorkList(p: WorkPeriod, metric: WorkMetric | null, employee: string | null) {
  return useQuery({
    queryKey: ['sales-performance', 'activity-list', p, metric, employee],
    enabled: Boolean(metric),
    queryFn: async () => {
      const q = workQuery(p);
      q.set('metric', metric as string);
      if (employee) q.set('employee', employee);
      return (await api.get<{ label: string; total: number; items: WorkItem[] }>(`/api/sales-performance/activity/list?${q}`)).data;
    },
  });
}
